package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.ImageHeader
import dev.lingvo.shared.ImageHeaderResult
import dev.lingvo.shared.ImageHeaders
import dev.lingvo.shared.ScreenshotSpecs
import dev.lingvo.shared.ShotIssueCode
import dev.lingvo.shared.ShotPurpose
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.t

/** Distinguishes a prefix needing the rest of the bounded file from a genuine refusal. */
sealed interface CapturePrecheck {
    /** Full-container parsing needs bytes after the first 64 KiB; no request has started. */
    data object NeedFullFile : CapturePrecheck
    /** The same header and size table as the server validated this input. */
    data class Accepted(val header: ImageHeader, val warnings: List<String>) : CapturePrecheck
    /** The user gets a repair sentence before any upload begins. */
    data class Refused(val message: String) : CapturePrecheck
}

/** Limits allocation before reading and never mistakes a truncated prefix for a malformed whole image. */
fun precheckCapture(bytes: ByteArray, byteSize: Long, device: DeviceClass): CapturePrecheck {
    if (byteSize > ScreenshotSpecs.MAX_IMAGE_BYTES) return CapturePrecheck.Refused(trf("screenshots.upload.too_large",
        "This file is {size} MB. Captures are limited to {limit} MB.",
        "size" to ((byteSize * 10 / (1024 * 1024)).toDouble() / 10).toString(),
        "limit" to ScreenshotSpecs.MAX_IMAGE_BYTES / (1024 * 1024)))
    val result = ImageHeaders.parse(bytes)
    if (result is ImageHeaderResult.Refused) {
        if (bytes.size < byteSize && result.issue.code == ShotIssueCode.TRUNCATED) return CapturePrecheck.NeedFullFile
        return CapturePrecheck.Refused(result.issue.message)
    }
    val header = (result as ImageHeaderResult.Ok).header
    val issues = ScreenshotSpecs.check(device, header, byteSize.toInt(), ShotPurpose.CAPTURE)
    if (issues.isNotEmpty()) return CapturePrecheck.Refused(if (issues.first().code == ShotIssueCode.WRONG_SIZE) {
        trf("screenshots.upload.wrong_size", "This capture is {width} × {height}. {device} needs {wanted}. Capture on a matching simulator and upload again.",
            "width" to header.width, "height" to header.height, "device" to screenshotDeviceLabel(device),
            "wanted" to ScreenshotSpecs.describe(device.spec.captureRule))
    } else issues.first().message)
    return CapturePrecheck.Accepted(header, ScreenshotSpecs.warnings(device, header, ShotPurpose.CAPTURE).map { it.message })
}

/** Quota refusals retain server numbers; other server repair sentences stay intact. */
fun captureUploadFailure(failure: Throwable): String = when {
    failure is ApiException && failure.code == "screenshot_quota_exceeded" -> screenshotsQuotaExceededMessage(failure.details)
    failure is ApiException -> failure.message
    else -> tr("app.projects.slug.screenshots.capture_upload_model.couldnt_upload_this_capture_retry_when", "Couldn't upload this capture. Retry when you're connected.")
}

/** Per-file state makes cancellation and retry independent of the rest of a batch. */
enum class CaptureUploadPhase { READY, CHECKING, UPLOADING, DONE, FAILED, CANCELLED }

/** Bounds preview DOM and directory traversal to the same entry ceiling as screenshot ZIP import/export. */
const val MAX_CAPTURE_BATCH: Int = 2000

/** Rejects an oversized batch explicitly rather than silently dropping selected files. */
fun captureBatchLimitMessage(count: Int): String? = if (count > MAX_CAPTURE_BATCH) trf("screenshots.upload.batch_limit",
    "Choose at most {limit} captures at a time. You selected {count}.", "limit" to MAX_CAPTURE_BATCH, "count" to count) else null

/** Merges the stored upload response into the metadata used by replacement claims and delete confirmations. */
fun mergeUploadedCaptures(captures: List<dev.lingvo.shared.CaptureDto>, uploaded: dev.lingvo.shared.CaptureDto): List<dev.lingvo.shared.CaptureDto> =
    listOf(uploaded) + captures.filter { capture ->
        capture.id != uploaded.id && (capture.slotId != uploaded.slotId || capture.locale != uploaded.locale ||
            capture.deviceClass != uploaded.deviceClass || capture.id == uploaded.supersedesCaptureId)
    }
